import { Card, Box, Link, Typography, IconButton, Tooltip } from "@mui/material"
import { ListContextProvider, ReduxState, useListContext } from "ra-core"
import { FC, useMemo } from "react"
import { Content } from "@/components/content/Content"
import { FunctionField } from "@/components/field"
import { Datagrid } from "@/components/list/datagrid"
import ScheduleEncryption from "@/components/obfuscation/ScheduleEncryption"
import Scrollbar from "@/components/Scrollbar"
import { RecPatch } from "@/dataProvider/tools"
import { EncryptionJobResponseDto, EncryptionStatus } from "@/gen/api/encrypt"
import { OdResourcePageProps } from "@/types/resource"
import { Link as RouterLink } from 'react-router-dom'
import ArrowRightIcon from '@/icons/ArrowRight';
import DateField2 from "@/components/field/DateField2"
import LogViewer, { useLogViewerController, LogViewerProvider, useLogViewerContext } from "@/components/backups/LogViewer"
import { useResourceRegister } from "@/utils/resourceRegister"
import useLocalListController from "@/components/list/useLocalListController"
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import SizeTextField from "@/components/field/SizeTextField"
import { AtopFuncArea, FilterField, FilterOption, SearchField } from "@/components/list/datagridFunctionWidgets"
import { useSelector } from "react-redux"
import { genUrlWithHashid } from "@/utils/hashid"
import DatabaseNameField from "@/components/databases/DatabaseNameField"
import { EncryptedDatabasesContextProvider } from "@/components/databases/useEncryptedDatabases"
import { useHasPermit } from "@/authProvider/useHasPermit"
import { SCHEDULE_ENCRYPTION_OBFUSCATION } from "@/constants"


const databaseListOptions : {sorters: Record<string, (a:EncryptionJobResponseDto & RecPatch, b:EncryptionJobResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
    name: (a, b) => (a.name || '') < (b.name || '') ? -1 : 1,
    // sizeText: (a, b) => (a.size || 0) < (b.size || 0) ? -1 : 1,
    lastEncryptionTime: (a, b) => (a.lastEncryptionTime || '') < (b.lastEncryptionTime || '') ? -1 : 1,
  }
}

const EncryptionList: FC<OdResourcePageProps> = (props) => {
  const { resource } = props
  useResourceRegister({ name: 'encryptable-backups' })
  
  const listCtrl = useLocalListController({ resource, basePath: '' }, databaseListOptions)

  const logCtrl = useLogViewerController()

  return (
    <LogViewerProvider value={logCtrl}>
      <ListContextProvider value={listCtrl}>
        <EncryptionListBody {...props} />
        <LogViewer />
      </ListContextProvider>
    </LogViewerProvider>
  )
}

export interface EncryptionListBodyProps extends OdResourcePageProps {
}

const statusOptions = [
  { label: 'No Encryption', value: EncryptionStatus.NotStarted },
  { label: 'In Progress', value: EncryptionStatus.InProgress },
  { label: 'Success', value: EncryptionStatus.Success },
  { label: 'Failed', value: EncryptionStatus.Failed },
  { label: 'Warning', value: EncryptionStatus.Warning },
]

export const EncryptionListBody: FC<EncryptionListBodyProps> = ({
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([SCHEDULE_ENCRYPTION_OBFUSCATION])
  const listCtx = useListContext<EncryptionJobResponseDto & RecPatch>()
  const { showLogInViewer } = useLogViewerContext()

  const data = useSelector<ReduxState, Record<number, EncryptionJobResponseDto & RecPatch>>(store => store.admin.resources['encryption/list'].data)
  const encryptDbs = Object.values(data || {})

  const engineOptions = useMemo(() => Object.values(
    encryptDbs.reduce((acc, d) => {
      if (d.engineId) {
        const option = ({
          label: `${d.engineVersion || ''}${d.engineEdition ? ' ' + d.engineEdition : ''}`,
          value: d.engineId
        })
        acc[d.engineId] = option
      }
      return acc
    }, {} as Record<number, {label:string,value:number}>)
  ).sort((a, b) => a.label < b.label ? -1 : 1), [ JSON.stringify([...new Set(encryptDbs.map(d => d.engineId))].sort()) ])
  
  
  return (
    <Content 
      pageName="Encryption Jobs" 
      resourceName="Encryption Jobs" 
      breadcrumbs={options?.breadcrumbs}
      mainActions={(
        hasWritePermit &&
        <ScheduleEncryption />
      )}
    >
      <EncryptedDatabasesContextProvider>
        <Card sx={{ mt: 2 }}>
          <AtopFuncArea>
            <SearchField 
              sourceKey="dbName"
              match={(d: EncryptionJobResponseDto, fval:string) => !!d.name && d.name.toLowerCase().includes(fval.trim().toLowerCase())}
              sx={{ width: 280 }}
              TextFieldProps={{ placeholder: 'Search by Database Name' }}
            />
            <FilterField 
              label="Engine" 
              filterOptions={engineOptions} 
              sourceKey="engine"
              match={(d: EncryptionJobResponseDto, fval:FilterOption) => d.engineId === fval.value}
              sx={{ width: 320 }}
            />
            <FilterField 
              label="Status" 
              filterOptions={statusOptions} 
              sourceKey="status"
              match={(d: EncryptionJobResponseDto, fval:FilterOption) => d.encryptionStatus === fval.value}
              sx={{ width: 160 }}
            />
          </AtopFuncArea>
          <Scrollbar options={{ suppressScrollY: true }}>
            <Box sx={{ minWidth: 1150 }}>
              <Datagrid 
                hasBulkActions={false} 
                sx={{ 
                  '& th' : { py: 2 },
                  '& td' : { py: 2 },
                }}
                {...listCtx}
              >
                <DatabaseNameField label="Database" />
                <FunctionField 
                  source="engineVersion" 
                  label="Engine" 
                  sortable={false} 
                  render={(d?: EncryptionJobResponseDto & RecPatch) => (
                    <Typography
                      component="p"
                      variant="body2"
                    >
                      {d?.engineVersion + ' ' + (d?.engineEdition || '')}
                    </Typography>
                  )}
                />
                <SizeTextField source="sizeText" numericSource="size" label="Size" emptyText="N/A" sortable={false} />
                <DateField2 source="lastEncryptionTime" label="Last Encryption" showTime />
                <FunctionField 
                  label="Status" 
                  textAlign="center"
                  sortable={false} 
                  render={(d?: EncryptionJobResponseDto & RecPatch) => (
                    <Box sx={{ display: 'flex!important', flexDirection: 'column', alignItems: 'center' }}>
                      <Tooltip title={statusOptions.find(x => x.value === (d?.encryptionStatus || EncryptionStatus.NotStarted))?.label || ''}>
                        <Box
                          component="span"
                          role="figure"
                          sx={{
                            bgcolor:
                              d?.encryptionStatus === EncryptionStatus.Failed ? "error.main" 
                              : d?.encryptionStatus === EncryptionStatus.Success ? "success.main" 
                              : d?.encryptionStatus === EncryptionStatus.Warning ? "warning.main" 
                              : "action.disabled",
                            width: 24, 
                            height: 24, 
                            display: 'inline-block',
                            borderRadius: '50%',
                            mr: 1,
                            boxShadow: theme => theme.shadows["4"],
                          }}
                        />
                      </Tooltip>
                      {d?.encryptionStatus === EncryptionStatus.Failed ?
                        <Link
                          component="button"
                          color="secondary"
                          underline="hover"
                          onClick={(e:any) => showLogInViewer(e, d.encryptionFailedLog || '')}
                          sx={{ cursor: 'pointer'}}
                        >
                          <Typography
                            variant="caption"
                          >
                            (View Log)
                          </Typography> 
                        </Link> : null
                      }
                    </Box>
                  )}
                />
                <FunctionField 
                  label="Action"
                  sortable={false}
                  render={(d?: EncryptionJobResponseDto & RecPatch) => (
                    <Tooltip title="Details">
                      <IconButton 
                        sx={{ 
                          color: 'text.primary' as any, 
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        component={RouterLink}
                        to={genUrlWithHashid(`/encryption/list/$0/show`, d?.databaseId || 0, d?.name)}
                      >
                        <ArrowRightIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )} 
                />
              </Datagrid>
            </Box>
          </Scrollbar>
          {//@ts-ignore 
          <DefaultPagination {...listCtx} />}
        </Card>
      </EncryptedDatabasesContextProvider>
    </Content>
  )
}

export default EncryptionList;
